import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listChatLastMessages } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { ChatThread } from "@/components/chat-thread";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/chat")({ component: OperatorChat });

function OperatorChat() {
  const { tenantId, customers } = useScope();
  const tenantCustomers = customers.filter((c) => c.tenant_id === tenantId);
  const fetchLast = useServerFn(listChatLastMessages);

  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    if (!selected && tenantCustomers[0]) setSelected(tenantCustomers[0].id);
    if (selected && !tenantCustomers.find((c) => c.id === selected)) setSelected(tenantCustomers[0]?.id ?? null);
  }, [tenantCustomers, selected]);

  const { data: lastMessages = [] } = useQuery({
    queryKey: ["chat-last", tenantId],
    enabled: !!tenantId,
    queryFn: () => runServerFn(
      () => fetchLast({ data: { tenantId: tenantId! } }),
      "Loading chat",
    ),
  });

  const lastByCustomer = new Map<string, { body: string; created_at: string }>();
  for (const m of lastMessages) {
    if (!lastByCustomer.has(m.customer_id)) lastByCustomer.set(m.customer_id, { body: m.body, created_at: m.created_at });
  }

  return (
    <>
      <PageHeader title="Chat" description="Conversations with your brands." actions={<HelpButton moduleKey="chat" />} />
      <div className="p-4 sm:p-6">
        <div className="border rounded-lg bg-card grid grid-cols-[260px_1fr] h-[calc(100vh-220px)] overflow-hidden">
          <div className="border-r overflow-y-auto">
            {tenantCustomers.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No brands in this tenant.</div>
            ) : tenantCustomers.map((c) => {
              const last = lastByCustomer.get(c.id);
              const active = c.id === selected;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelected(c.id)}
                  className={cn(
                    "w-full text-left px-4 py-3 border-b hover:bg-accent/50 transition-colors",
                    active && "bg-accent",
                  )}
                >
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {last?.body ?? "No messages yet"}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="min-w-0">
            {selected && tenantId ? (
              <ChatThread tenantId={tenantId} customerId={selected} senderKind="operator" />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Select a brand to start chatting.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}